import { useIsMobile, useOutsideClick } from "@/hooks";
import { useRecoilState, useSetRecoilState } from "recoil";
import { hoveredElementIdAtom } from "@/atoms/NavigationAtoms";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { IFirstLevelNavItem, INavigationDTO } from "@/interfaces/INavigationDTO";
import { MegaMenu, MobileSearchMenu, MobileSideMenu, Navigation, SearchMenu } from "@/components/index"
import { SearchMenuAtom } from "../../atoms";
import { useTranslation } from "react-i18next";
import { getCDNCategories } from "@/utils/getCDNToken";

interface INavigationProps {
    navigationData: INavigationDTO,
    hostPageData?: any,
    language: string
}

interface ICategoryDTO {
    id: INavigationDTO,
    custom_url: { url: string },
    description: string,
    name: string,
    is_visible: boolean,
    parent_id: number
}

const NavigationMenu: React.FC<INavigationProps> = ({ navigationData, hostPageData = null, language }: INavigationProps) => {

    const { i18n } = useTranslation();
    const lang = language? language : i18n.language;

    const setselectedElementId = useSetRecoilState(hoveredElementIdAtom)
    const headerRef = useRef<null | HTMLDivElement>(null);
    const [isMobile] = useIsMobile()
    const [categoryData, setCategorydata] = useState<ICategoryDTO[]>();
    const [isBoutiqueEnabled, setIsBoutiqueEnabled] = useState<boolean>(false);
    const [taxonomyItem] = navigationData?.data;

    const imageObj = useMemo(() => {
        let imageObj = taxonomyItem.ti.find((item: any) => item.label?.toLowerCase() === process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()).xtaxAirport?.ti?.upiLogoBlack;
        return {
            path: imageObj?.mediaserverpath + "/" + imageObj?.resourcename,
            description: "",
            alt: ""
        }
    }, [navigationData])

    const navItems: IFirstLevelNavItem[] = taxonomyItem.ti.find((item: any) => item.label?.toLowerCase() === process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()).ti;
    const [isSearchOpen, setIsSearchOpen] = useRecoilState(SearchMenuAtom);

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen)
    }

    useEffect(() => {

        // Recupera il flag Boutique dal CMS
        fetch(`${process.env.NEXT_PUBLIC_BE_BASE_PATH}/rest/v1/SEA-LinMxp/pages/web/boutique`)
            .then(response => response?.json())
            .then(response => {
                setIsBoutiqueEnabled(response?.data?.flgEnableBoutique || false);
            })
            .catch(error => {
                console.error('[NavigationMenu] getBoutiqueFlag error -> ', error);
                setIsBoutiqueEnabled(false);
            });

        // Determina il channel_id in base all'aeroporto
        const isMalpensa = process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase() === 'malpensa';
        const channelId = isMalpensa
            ? (process.env.NEXT_PUBLIC_CDN_CHANNEL_ID_MXP || '1770668')
            : (process.env.NEXT_PUBLIC_CDN_CHANNEL_ID_LIN || '1770669');

        const menuCategoryId = isMalpensa
            ? (process.env.NEXT_PUBLIC_CDN_CATEGORY_ID_MENU_MXP || '36')
            : (process.env.NEXT_PUBLIC_CDN_CATEGORY_ID_MENU_LIN || '46');

        getCDNCategories(channelId, menuCategoryId)
            .then(categories => {
                // Auto-detect il parent_id delle categorie boutique:
                // cerca la categoria root "boutique" tramite URL, poi filtra per i suoi figli diretti
                const boutiqueRoot = categories.find(cat =>
                    cat.custom_url?.url?.toLowerCase().includes('/boutique') &&
                    cat.parent_id === 0
                );

                if (boutiqueRoot) {
                    const boutiqueCategories = categories.filter(cat => cat.parent_id === boutiqueRoot.id);
                    console.log(`[NavigationMenu] Auto-detected parent_id: ${boutiqueRoot.id}, found ${boutiqueCategories.length} boutique categories`);
                    setCategorydata(boutiqueCategories as any);
                } else {
                    // Fallback: cerca il parent_id più frequente tra le categorie di primo livello
                    const parentIds = categories
                        .filter(cat => cat.parent_id !== 0)
                        .map(cat => cat.parent_id);
                    const mostCommon = parentIds.sort((a, b) =>
                        parentIds.filter(id => id === b).length - parentIds.filter(id => id === a).length
                    )[0];
                    const boutiqueCategories = categories.filter(cat => cat.parent_id === mostCommon);
                    console.log(`[NavigationMenu] Fallback parent_id: ${mostCommon}, found ${boutiqueCategories.length} boutique categories`);
                    setCategorydata(boutiqueCategories as any);
                }
            })
            .catch(error => {
                console.error('[NavigationMenu] getCDNCategories error -> ', error);
            });
    }, []);

    
function localizeCategoryEn(categories: any) {

    categories?.map((category: ICategoryDTO) => {
        if (!category.custom_url) {
            category.custom_url = { url: '' };
        }

        switch(category.name) {
            case 'Borse': {
                category.name = 'Bags';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            } 
            case 'Orologi': {
                category.name = 'Watches';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
            case 'Profumi': {
                category.name = 'Fragrances';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
            case 'Beauty': {
                category.custom_url.url = '/en/beauty';
                return category;
            }
            case 'Accessori': {
                category.name = 'Accessories';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
            case 'Alcolici': {
                category.name = 'Spirits';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
            case 'Food': {
                category.custom_url.url = '/en/food';
                return category;
            }
            case 'Abbigliamento': {
                category.name = 'Clothing';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
            case 'Gioielli e bigiotteria': {
                category.name = 'Jewelry';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
            case 'Scarpe': {
                category.name = 'Shoes';
                category.custom_url.url = '/en' + category.custom_url.url;
                return category;
            }
        }
    })

    return categories;
}

    useOutsideClick(headerRef as MutableRefObject<HTMLDivElement>, () => { setIsSearchOpen(false); setselectedElementId(null) });


    return (
        <header ref={headerRef} className='relative mx-auto max-w-cont-1 w-full '>
            <Navigation language={lang} onSearchClick={toggleSearch} hostPageData={hostPageData} navItems={navItems} imageUrl={imageObj} />
            <MegaMenu language={lang} navItems={navItems} categoryData={lang == 'it' ? categoryData : localizeCategoryEn(categoryData)} isBoutiqueEnabled={isBoutiqueEnabled} />
            {isSearchOpen && !isMobile ? <SearchMenu /> : null}
            <MobileSearchMenu language={lang}  isMobile={isMobile} imageUrl={imageObj} />
            <MobileSideMenu language={lang} hostPageData={hostPageData} menuItems={navItems} imageUrl={imageObj} categoryData={lang == 'it' ? categoryData : localizeCategoryEn(categoryData)} isBoutiqueEnabled={isBoutiqueEnabled} />
        </header>
    )
};

export default NavigationMenu;